import React, { useEffect, useMemo } from "react";

import { Grid } from "@mui/material";
import Containers from "Components/Generic/Containers";
import { formatInTimeZone } from "date-fns-tz";
import _ from "lodash";
import { MilestoneType, MilestoneValue } from "Models/milestone";
import { useTranslation } from "react-i18next";

import { Input } from "../../../../../../../../components/Form";
import {
  MilestoneDates,
  renderMilestoneDateByTypeAndReference,
} from "../../../../../../../../components/Generic/MilestoneDates";
import { Waybill } from "../../../../../../../../models/waybill";
import { useShipmentContext } from "../../../hooks/useContext";
import { DefaultProps } from "../../../types";
import { MilestoneWrapper, StringField, StringsLabel, StringsText } from "../styles";
import { ViewField } from "../ViewField";
import { GeneralSection } from "./components/general";
import { SectionTitle } from "./components/section-title";
import { ServiceSection } from "./components/service";

const sortActualDatesMileStoneOptions = (options: any[]) => {
  const order = ["actualDepartureDate", "actualArrivalOfDestination", "actualAtDepartureDate"];

  return options.sort((a, b) => {
    const indexA = order.indexOf(a.reference);
    const indexB = order.indexOf(b.reference);

    if (indexA !== -1 && indexB !== -1) {
      // Both items are in the custom order, sort by their index
      return indexA - indexB;
    }
    if (indexA !== -1) {
      // `a` is in the custom order, but `b` is not -> `a` comes first
      return -1;
    }
    if (indexB !== -1) {
      // `b` is in the custom order, but `a` is not -> `b` comes first
      return 1;
    }

    // If both are not in the custom order, sort alphabetically by reference
    return a.reference.localeCompare(b.reference);
  });
};

interface SeaFormInterface extends DefaultProps {
  waybillView?: Waybill;
}

const SeaForm = (props: SeaFormInterface) => {
  const { waybillView, onViewMode = false, errors = [], isFetchingReference } = props;
  const { t: translate } = useTranslation();

  const { shipment, milestones, setShipment } = useShipmentContext();
  const isShipmentTrackingFetched = useMemo(() => shipment?.fetchedData, [shipment]);
  const hasMarineTraffic = useMemo(() => !!shipment?.marineTrafficId, [shipment?.marineTrafficId]);

  const renderMilestoneValue = (type: MilestoneType) => {
    const waybillMilestones: MilestoneValue[] | undefined = waybillView?.milestones?.filter(
      (milestone) => milestone.milestone?.type === type
    );

    if (!waybillMilestones?.length) {
      return (
        <StringField>
          <StringsText isEmpty>{translate("empty")}</StringsText>
        </StringField>
      );
    }

    return waybillMilestones?.map((milestone) => (
      <StringField key={milestone.id}>
        <StringsLabel>{milestone.milestone?.title}</StringsLabel>
        <StringsText>
          {renderMilestoneDateByTypeAndReference(
            waybillView?.milestones ?? [],
            type,
            milestone.milestone?.reference ?? ""
          )}
        </StringsText>
      </StringField>
    ));
  };
  const renderMileStone = () => {
    return (
      <React.Fragment>
        {onViewMode ? (
          <React.Fragment>
            <Grid item xs={12}>
              <SectionTitle title={translate("estimatedDates")} />
            </Grid>
            <Grid item xs={12}>
              <MilestoneWrapper>
                <StringField key={"loadBy"}>
                  <StringsLabel>{translate("loadBy")}</StringsLabel>
                  <StringsText>
                    {shipment?.cargoReadyDate
                      ? formatInTimeZone(new Date(shipment?.cargoReadyDate), "UTC", "yyyy-MM-dd")
                      : ""}
                  </StringsText>
                </StringField>
                {renderMilestoneValue(MilestoneType.ESTIMATED)}
              </MilestoneWrapper>
            </Grid>
          </React.Fragment>
        ) : (
          <MilestoneDates
            prefixCustomTemplate={
              <Input
                type="date"
                label={translate("loadBy")}
                onChange={(event) =>
                  setShipment({
                    ...shipment,
                    cargoReadyDate: event.target.value
                      ? formatInTimeZone(new Date(event.target.value), "UTC", "yyyy-MM-dd")
                      : undefined,
                  })
                }
                value={
                  shipment?.cargoReadyDate
                    ? formatInTimeZone(new Date(shipment?.cargoReadyDate), "UTC", "yyyy-MM-dd")
                    : ""
                }
              />
            }
            type={MilestoneType.ESTIMATED}
            title={translate(`estimatedDates`)}
            values={!_.isEmpty(shipment?.milestones) ? shipment?.milestones || [] : []}
            fieldSize={3}
            options={milestones}
            onUpdate={(_, value) => {
              setShipment({ ...shipment, milestones: value ?? undefined });
            }}
          />
        )}

        {onViewMode ? (
          <React.Fragment>
            <Grid item xs={12}>
              <SectionTitle title={translate("actualDates")} />
            </Grid>
            <Grid container spacing={4} xs={12}>
              <Grid item xs={12} md={3} lg={3}>
                <ViewField
                  label={translate("dateOfLoading")}
                  value={
                    shipment?.shipmentTracking?.dateOfLoading
                      ? formatInTimeZone(
                          new Date(shipment?.shipmentTracking?.dateOfLoading),
                          "UTC",
                          "yyyy-MM-dd"
                        )
                      : ""
                  }
                />
              </Grid>
              <MilestoneWrapper>{renderMilestoneValue(MilestoneType.ACTUAL)}</MilestoneWrapper>
            </Grid>
          </React.Fragment>
        ) : (
          <MilestoneDates
            prefixCustomTemplate={
              <Input
                type="date"
                label={translate("dateOfLoading")}
                disabled={hasMarineTraffic || isShipmentTrackingFetched}
                value={
                  shipment?.shipmentTracking?.dateOfLoading
                    ? formatInTimeZone(
                        new Date(shipment?.shipmentTracking?.dateOfLoading),
                        "UTC",
                        "yyyy-MM-dd"
                      )
                    : ""
                }
              />
            }
            type={MilestoneType.ACTUAL}
            title={translate("actualDates")}
            values={shipment?.milestones || []}
            fieldSize={3}
            options={sortActualDatesMileStoneOptions(milestones)}
            onUpdate={(_, value) => {
              setShipment({ ...shipment, milestones: value ?? undefined });
            }}
          />
        )}
      </React.Fragment>
    );
  };

  const renderDatesFieldByFetchedData = () => {
    return onViewMode ? (
      <>
        <Grid item xs={12}>
          <SectionTitle title={translate("estimatedDates")} />
        </Grid>
        <Grid container spacing={4} xs={12}>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("loadBy")}
              value={
                shipment?.cargoReadyDate
                  ? formatInTimeZone(new Date(shipment?.cargoReadyDate), "UTC", "yyyy-MM-dd")
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("estimateTimeOfDeparture")}
              value={
                shipment?.shipmentTracking?.originPortPredictiveDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortPredictiveDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("estimateTimeOfArrival")}
              value={
                shipment?.shipmentTracking?.finalPortPredictiveArrivalLast
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.finalPortPredictiveArrivalLast),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <SectionTitle title={translate("actualDates")} />
        </Grid>
        <Grid container spacing={4} xs={12} item>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("dateOfLoading")}
              value={
                shipment?.shipmentTracking?.dateOfLoading
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.dateOfLoading),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("departureDate")}
              value={
                shipment?.shipmentTracking?.originPortActualDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortActualDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("atDepartureDock")}
              value={
                shipment?.shipmentTracking?.originPortActualDeparture
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.originPortActualDeparture),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3}>
            <ViewField
              label={translate("arrivalAtDestination")}
              value={
                shipment?.shipmentTracking?.finalPortActualArrival
                  ? formatInTimeZone(
                      new Date(shipment?.shipmentTracking?.finalPortActualArrival),
                      "UTC",
                      "yyyy-MM-dd"
                    )
                  : ""
              }
            />
          </Grid>
        </Grid>
      </>
    ) : (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "40px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Grid item xs={12}>
            <SectionTitle title={translate("estimatedDates")} />
          </Grid>
          <Grid item container direction="row" xs={12} spacing={2}>
            <Grid item xs={12} md={3} lg={3}>
              <Input
                type="date"
                label={translate("loadBy")}
                onChange={(event) =>
                  setShipment({
                    ...shipment,
                    cargoReadyDate: event.target.value
                      ? formatInTimeZone(new Date(event.target.value), "UTC", "yyyy-MM-dd")
                      : undefined,
                  })
                }
                value={
                  shipment?.cargoReadyDate
                    ? formatInTimeZone(new Date(shipment?.cargoReadyDate), "UTC", "yyyy-MM-dd")
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <Input
                type="date"
                label={translate("estimateTimeOfDeparture")}
                disabled={hasMarineTraffic || isShipmentTrackingFetched}
                value={
                  shipment?.shipmentTracking?.originPortPredictiveDeparture
                    ? formatInTimeZone(
                        new Date(shipment?.shipmentTracking?.originPortPredictiveDeparture),
                        "UTC",
                        "yyyy-MM-dd"
                      )
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <Input
                type="date"
                label={translate("estimateTimeOfArrival")}
                disabled={hasMarineTraffic || isShipmentTrackingFetched}
                onChange={(event) =>
                  setShipment({
                    ...shipment,
                    eta: event.target.value
                      ? formatInTimeZone(new Date(event.target.value), "UTC", "yyyy-MM-dd")
                      : undefined,
                    shipmentTracking: {
                      ...shipment?.shipmentTracking,
                      finalPortPredictiveArrivalLast: event.target.value,
                    },
                  })
                }
                value={
                  shipment?.shipmentTracking?.finalPortPredictiveArrivalLast
                    ? formatInTimeZone(
                        new Date(shipment?.shipmentTracking?.finalPortPredictiveArrivalLast),
                        "UTC",
                        "yyyy-MM-dd"
                      )
                    : ""
                }
              />
            </Grid>
          </Grid>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Grid item xs={12}>
            <SectionTitle title={translate("actualDates")} />
          </Grid>
          <Grid item container direction="row" xs={12} spacing={2}>
            <Grid item xs={12} md={3} lg={3}>
              <Input
                type="date"
                label={translate("dateOfLoading")}
                disabled={hasMarineTraffic || isShipmentTrackingFetched}
                value={
                  shipment?.shipmentTracking?.dateOfLoading
                    ? formatInTimeZone(
                        new Date(shipment?.shipmentTracking?.dateOfLoading),
                        "UTC",
                        "yyyy-MM-dd"
                      )
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <Input
                type="date"
                label={translate("departureDate")}
                disabled={hasMarineTraffic || isShipmentTrackingFetched}
                onChange={(event) => {
                  setShipment({
                    ...shipment,
                    etd: formatInTimeZone(new Date(event.target.value), "UTC", "yyyy-MM-dd"),
                    shipmentTracking: {
                      ...shipment?.shipmentTracking,
                      originPortActualDeparture: event.target.value,
                    },
                  });
                }}
                value={
                  shipment?.shipmentTracking?.originPortActualDeparture
                    ? formatInTimeZone(
                        new Date(shipment?.shipmentTracking?.originPortActualDeparture),
                        "UTC",
                        "yyyy-MM-dd"
                      )
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <Input
                type="date"
                label={translate("atDepartureDock")}
                disabled={hasMarineTraffic || isShipmentTrackingFetched}
                value={
                  shipment?.shipmentTracking?.originPortActualDeparture
                    ? formatInTimeZone(
                        new Date(shipment?.shipmentTracking?.originPortActualDeparture),
                        "UTC",
                        "yyyy-MM-dd"
                      )
                    : ""
                }
              />
            </Grid>

            <Grid item xs={12} md={3} lg={3}>
              <Input
                type="date"
                label={translate("arrivalAtDestination")}
                disabled={hasMarineTraffic || isShipmentTrackingFetched}
                onChange={(event) => {
                  setShipment({
                    ...shipment,

                    shipmentTracking: {
                      ...shipment?.shipmentTracking,
                      finalPortActualArrival: event.target.value,
                    },
                  });
                }}
                value={
                  shipment?.shipmentTracking?.finalPortActualArrival
                    ? formatInTimeZone(
                        new Date(shipment?.shipmentTracking?.finalPortActualArrival),
                        "UTC",
                        "yyyy-MM-dd"
                      )
                    : ""
                }
              />
            </Grid>
          </Grid>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (onViewMode && waybillView) {
      setShipment(waybillView);
    }
  }, [waybillView, onViewMode]);

  return (
    <React.Fragment>
      <Grid item container direction="column" rowGap={5}>
        <Grid item container direction="column" rowGap={3}>
          <GeneralSection
            errors={errors}
            isOnView={onViewMode}
            isFetchingReference={isFetchingReference}
          />
        </Grid>

        <Grid item container direction="column" rowGap={3}>
          <ServiceSection errors={errors} isOnView={onViewMode} />

          <Containers
            shipment={shipment}
            isView={onViewMode}
            errors={errors}
            handleChangeOptions={() => {}}
            handleUpdateShipment={(attribute, containers) => {
              if (attribute === "all") setShipment({ ...shipment, ...containers });
              else setShipment({ ...shipment, containers });
            }}
          />
        </Grid>
        <Grid item container direction="column" rowGap={5}>
          {isShipmentTrackingFetched || shipment?.shipmentTrackingId
            ? renderDatesFieldByFetchedData()
            : renderMileStone()}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default SeaForm;
